/**
 * Calendar utility functions
 */

export interface CalendarEventParams {
  date: any;
  description: any;
  location: any;
  title: any;
}

/**
 * Opens Google Calendar with a pre-filled event template
 * Handles popup blockers gracefully with fallback options
 *
 * @param date - Event date (can be string, Date object, or ISO string)
 * @param description - Event description
 * @param location - Event location
 * @param title - Event title
 */
export function addToCalendarClick(date: any, description: any, location: any, title: any): void {
  /* Add to calendar needs the following format:
  https://calendar.google.com/calendar/render?action=TEMPLATE
    &dates=20230202T064500Z%2F20230202T071500Z
    &details=This%20is%20my%20description
    &location=home
    &text=Title */

  try {
    // Handle different date formats and convert to ISO string
    let isoDate: string;
    if (typeof date === 'string') {
      // Remove 'Z' if already present to avoid duplication
      const cleanDate = date.endsWith('Z') ? date.slice(0, -1) : date;
      isoDate = new Date(cleanDate).toISOString();
    } else {
      isoDate = new Date(date).toISOString();
    }

    // Format date for Google Calendar: YYYYMMDDTHHMMSSZ
    const formattedDate = `${isoDate.replace(/[-:]/g, '').split('.')[0]}Z`;

    // Create end date (1 hour after start date for events)
    const startDate = new Date(isoDate);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
    const formattedEndDate = `${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;

    // URL encode parameters properly
    const encodedTitle = encodeURIComponent(title || '');
    const encodedDescription = encodeURIComponent(description || '');
    const encodedLocation = encodeURIComponent(location || '');

    const calendarBaseUrl = import.meta.env.VITE_GOOGLE_CALENDAR_BASE_URL || 'https://calendar.google.com/calendar';
    const addToCalendarURL = `${calendarBaseUrl}/render?action=TEMPLATE&dates=${formattedDate}%2F${formattedEndDate}&text=${encodedTitle}&details=${encodedDescription}&location=${encodedLocation}`;

    // Try to open in new tab first, fallback to current window with user confirmation
    const newWindow = window.open(addToCalendarURL, '_blank');

    // Check if popup was blocked
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      // Popup was likely blocked, ask user for permission to navigate
      const userConfirmed = confirm('Popup blocked. Would you like to open the calendar in the current window? (This will navigate away from this page)');
      if (userConfirmed) {
        window.location.href = addToCalendarURL;
      } else {
        // Provide fallback link
        console.log('Calendar link:', addToCalendarURL);
        alert(`Please copy this link to add to your calendar: ${addToCalendarURL}`);
      }
    }
  } catch (error) {
    console.error('Error creating calendar link:', error);
    // Fallback to simple alert
    alert('Unable to create calendar event. Please add the event manually to your calendar.');
  }
}
